import { useMemo, useState } from 'react'
import cx from 'classnames'
import clone from 'lodash/clone'
import cloneDeep from 'lodash/cloneDeep'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import set from 'lodash/set'
import unset from 'lodash/unset'

import CatalogHeader from './CatalogHeader'
import RecursiveSubMenu from './RecursiveSubMenu'
import SearchResults from './SearchResults'

export interface TreeNode {
  [key: string]: TreeNode | string[]
}

type Props = {
  items: TreeNode
  title?: string
  selectedItems?: TreeNode
  onApply?: (node: TreeNode) => void
  searchValue?: string
}
type SearchResult = { value: string; path: string[] }

const Catalog = ({
  items,
  onApply,
  selectedItems,
  searchValue,
  title,
}: Props) => {
  const [selectedPath, setSelectedPath] = useState<string[]>([])
  const [localSelectedItems, setLocalSelectedItems] =
    useState<TreeNode | undefined>(selectedItems)

  const searchResults: SearchResult[] | undefined = useMemo(() => {
    if (!searchValue) {
      return undefined
    }
    const search = (
      tree: TreeNode,
      searchValue: string,
      path: string[]
    ): SearchResult[] => {
      const lowerCaseSearchValue = searchValue.toLowerCase()
      return Object.keys(tree).flatMap((key) => {
        const matches: SearchResult[] = key
          .toLowerCase()
          .includes(lowerCaseSearchValue)
          ? [{ value: key, path: path }]
          : []
        const subTree = tree[key]
        const newPath = path.length === 0 ? [key] : [...path, key]
        const children: SearchResult[] = Array.isArray(subTree)
          ? subTree
              .filter((value) =>
                value.toLowerCase().includes(lowerCaseSearchValue)
              )
              .map((value) => ({
                value,
                path: newPath,
              }))
          : search(subTree, searchValue, newPath)
        return matches.concat(children)
      })
    }
    return search(items, searchValue, [])
  }, [searchValue, items])

  return (
    <div
      className={cx('w-full', {
        'bg-blueGray-100': !!selectedItems,
      })}
    >
      {selectedItems && title && localSelectedItems && (
        <CatalogHeader
          title={title}
          onApply={() => onApply?.(localSelectedItems)}
          isApplyDisabled={isEqual(localSelectedItems, selectedItems)}
          isResetDisabled={isEqual(localSelectedItems, selectedItems)}
          isSelectAllChecked={isEqual(localSelectedItems, items)}
          isSelectAllDisabled={isEqual(localSelectedItems, items)}
          onClickSelectAll={() => setLocalSelectedItems(items)}
          onClickReset={() => setLocalSelectedItems(selectedItems)}
        />
      )}
      <div className="w-full overflow-auto">
        <div
          className={cx(
            'bg-blueGray-100 flex h-full box-border border border-coolGray-200',
            {
              'w-max': !searchResults,
              'items-stretch flex-col': searchResults,
            }
          )}
        >
          {searchResults && localSelectedItems ? (
            <SearchResults
              items={items}
              selectedItems={localSelectedItems}
              setSelectedItems={setLocalSelectedItems}
              searchResults={searchResults}
            />
          ) : (
            <RecursiveSubMenu
              items={items}
              checkedItems={localSelectedItems}
              selectedPath={selectedPath}
              isSearching={searchResults !== undefined}
              onClickCheckbox={
                !selectedItems
                  ? undefined
                  : (itemName: string, depth: number) => {
                      if (!localSelectedItems) {
                        return
                      }
                      const result = cloneDeep(localSelectedItems)
                      if (depth === 0) {
                        if (result[itemName]) {
                          delete result[itemName]
                        } else {
                          result[itemName] = items[itemName]
                        }
                      } else {
                        const parentSelectedPath = selectedPath.slice(0, depth)
                        const parentSelectedItem = get(
                          localSelectedItems,
                          parentSelectedPath
                        )
                        const isChecked =
                          parentSelectedItem === undefined
                            ? false
                            : Array.isArray(parentSelectedItem)
                            ? parentSelectedItem.some(
                                (current) => current === itemName
                              )
                            : parentSelectedItem[itemName] !== undefined

                        if (!isChecked) {
                          const itemClicked =
                            get(items, selectedPath.slice(0, depth)) ?? []
                          Array.isArray(itemClicked)
                            ? set(result, selectedPath, [
                                ...(get(
                                  localSelectedItems,
                                  selectedPath.slice(0, depth)
                                ) ?? []),
                                itemName,
                              ])
                            : set(
                                result,
                                [...parentSelectedPath, itemName],
                                get(items, [...parentSelectedPath, itemName])
                              )
                        } else {
                          const children: TreeNode = get(
                            localSelectedItems,
                            parentSelectedPath
                          )
                          let filtered
                          if (Array.isArray(children)) {
                            filtered = children.filter((a) => a !== itemName)
                          } else {
                            const clonedChildren = clone(children)
                            delete clonedChildren[itemName]
                            filtered = clonedChildren
                          }

                          set(result, parentSelectedPath, filtered)

                          // delete empty nodes
                          if (isEmpty(filtered)) {
                            selectedPath.forEach((_, index) => {
                              const path = parentSelectedPath.slice(
                                0,
                                parentSelectedPath.length - index
                              )
                              const parent = get(result, path)

                              if (isEmpty(parent)) {
                                const parentOfParentPath = selectedPath.slice(
                                  0,
                                  selectedPath.length - index
                                )
                                unset(result, parentOfParentPath)
                              }
                            })
                          }
                        }
                      }
                      setLocalSelectedItems(result)
                    }
              }
              onSelect={(item: string, depth: number) => {
                if (depth === 0) {
                  setSelectedPath([item])
                } else {
                  setSelectedPath((prev) => [...prev.slice(0, depth), item])
                }
              }}
              depth={0}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default Catalog
